<template>
  <div>
    <b-overlay
      :show="isBusyTable"
      rounded="sm"
    >
      <!-- Tabla de materias -->
      <b-card
        no-body
        class="mb-0"
      >
        <transition name="fade-bottom">
          <section v-show="!idPlanSelected">
            <!-- Datos cargados -->
            <b-card-header class="pb-0">
              <b-card-title />
              <b-card-text class="font-small-2 mr-25 mb-0">
                Datos cargados <b>{{ updatedAt }}</b>
              </b-card-text>
            </b-card-header>

            <!-- Table Top -->
            <div class="m-2">
              <b-row>
                <!-- Cantidad de elementos por página -->
                <b-col
                  cols="12"
                  md="6"
                  class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
                >
                  <span>Mostrar</span>
                  <v-select
                    v-model="perPage"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="perPageOptions"
                    :clearable="false"
                    class="per-page-selector d-inline-block mx-50"
                  />
                </b-col>

                <!-- Buscador -->
                <b-col
                  cols="12"
                  md="6"
                >
                  <div class="d-flex align-items-center justify-content-end">
                    <b-input-group class="input-group-merge">
                      <b-input-group-prepend is-text>
                        <feather-icon
                          :class="{ 'text-danger cursor-pointer': searchQuery != '' }"
                          :icon="searchQuery != '' ? 'XIcon' : 'SearchIcon'"
                          @click="searchQuery != '' ? searchQuery = '' : ''"
                        />
                      </b-input-group-prepend>
                      <b-form-input
                        v-model="searchQuery"
                        placeholder="Buscar"
                      />
                    </b-input-group>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Table items -->
            <b-table
              ref="refDataListTable"
              class="position-relative"
              :items="fetchData"
              responsive
              :fields="tableColumns"
              primary-key="id"
              :sort-by.sync="sortBy"
              show-empty
              empty-text="No se encontraron resultados"
              :sort-desc.sync="isSortDirDesc"
            >
              <!-- Column: ID y edición -->
              <template #cell(id)="data">
                <div class="text-nowrap text-left">
                  <b-link
                    class="font-weight-bold text-secondary"
                    @click="idPlanSelected = data.item.id"
                  >
                    <feather-icon
                      :id="`plan-row-${data.item.id}-edit-icon`"
                      icon="SettingsIcon"
                      class="cursor-pointer mr-1"
                      size="16"
                    />
                  </b-link>
                  <b-tooltip
                    title="Editar registro"
                    placement="right"
                    :target="`plan-row-${data.item.id}-edit-icon`"
                  />
                  <span>{{ data.item.id }}</span>
                </div>
              </template>

              <!-- Column: Año y mes de inicio -->
              <template #cell(aamm)="data">
                <div class="text-center">
                  <b-badge
                    variant="light-primary"
                    style="display: inline;"
                    class="mr-25"
                  >
                    <b>{{ data.item.aamm }}</b>
                  </b-badge>
                </div>
              </template>

              <!-- Column: Estatus del plan -->
              <template #cell(activo)="data">
                <div class="text-center">
                  <b-badge :variant="`light-${(data.item.activo) ? `primary` : `danger`}`">
                    <feather-icon
                      icon="PowerIcon"
                      class="mr-25"
                    />
                    <span>{{ (data.item.activo) ? 'Activo' : 'Inactivo' }}</span>
                  </b-badge>
                </div>
              </template>
            </b-table>

            <!-- Table's footer & pagination -->
            <div class="mx-2 mb-2">
              <b-row>
                <!-- Leyenda de cuantos elementos se muestran -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span class="text-muted">Mostrando {{ dataMeta.from }} a {{ dataMeta.to }} de {{ dataMeta.of }} registros</span>
                </b-col>
                <!-- Pagination -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                >

                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalItems"
                    :per-page="perPage"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                    <template #next-text>
                      <feather-icon
                        icon="ChevronRightIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>

                </b-col>
              </b-row>
            </div>
          </section>
        </transition>
      </b-card>

      <!-- Edicion de un plan de estudios -->
      <transition name="fade-bottom">
        <section v-if="idPlanSelected">
          <!-- Alerta de guardado y liga a listado -->
          <b-alert
            variant="danger"
            show
          >
            <div class="alert-body">
              Una vez haya terminado la edición del plan de estudios, <b>no olvide guardar sus cambios</b> y <b
                style="text-decoration: underline;"
                @click="idPlanSelected = null; refetchData(true); updatedAt = lastUpdate()"
              ><a>presionar aquí</a></b> para volver al listado de planes.
            </div>
          </b-alert>
          <!-- Edición de plan (steps) -->
          <edit-plan
            :id.sync="idPlanSelected"
            @refetch-data="refetchData(true); updatedAt = lastUpdate()"
          />
        </section>
      </transition>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BBadge,
  BAlert,
  BPagination,
  BCardHeader,
  BCardText,
  BCardTitle,
  BTooltip,
  BInputGroup,
  BInputGroupPrepend,
  BLink,
  BOverlay,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, onMounted, watch } from '@vue/composition-api'
import { lastUpdate } from '@core/utils/filter'
import { isEmptyObject } from '@core/utils/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useFillDataTable } from '@core/components/data-table/useFillDataTable'
import { configTablePlanes } from './usePlanes'
import EditPlan from './components/EditPlan.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BBadge,
    BAlert,
    BCardHeader,
    BCardTitle,
    BCardText,
    BInputGroup,
    BInputGroupPrepend,
    BTooltip,
    BLink,
    BPagination,
    vSelect,
    BOverlay,
    EditPlan,
  },
  methods: {
    confirmDeleteAsignature(asignature) {
      this.$swal({
        title: '¿Está seguro de eliminar?',
        html: `Si continúa se eliminará la materia: <b>"${asignature.nombre}"</b>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteAsignature({ action: 5, materia: asignature.id })
        }
      })
    },
    deleteAsignature(dataModule) {
      this.isBusyTable = true
      // eslint-disable-next-line no-param-reassign
      dataModule.task = 'wsmaterias'

      this.$store.dispatch('servicioEscolar/postServicioEscolar', dataModule)
        .then(response => {
          this.isBusyTable = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Solicitud exitosa',
              text: response.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          },
          { timeout: 8000 })
          this.refetchData(true); this.updatedAt = lastUpdate()
        }, error => {
          this.isBusyTable = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error en petición [CRUD Materias]',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    },
  },
  setup() {
    const updatedAt = ref(lastUpdate())
    const loader = ref({ filters: false })
    const errorContent = ref({ filters: false })
    const idPlanSelected = ref(null)
    const { tableColumns, configRequest } = configTablePlanes()
    const {
      //* Fetching data
      fetchData, refetchData, refDataListTable, dataAux,
      //* Pagination
      dataMeta,
      //* Table dynamic params
      dynamicParamRequest,
      //* Basic filters & pagination
      perPage, totalItems, currentPage, perPageOptions, searchQuery, sortBy, isSortDirDesc,
      //* Loader
      isBusyTable,
    } = useFillDataTable()

    //* On mounted
    onMounted(() => {
      dynamicParamRequest.value = configRequest.value //* Asign config params for each module as dynamic params
    })

    watch(configRequest.value, () => { refetchData() }) //* Any change in config params (filters), refetch table data

    return {
      fetchData,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDataListTable,
      refetchData,
      dataAux,

      // Filter
      updatedAt,
      lastUpdate,

      // Extra Filters
      idPlanSelected,
      isEmptyObject,

      // Loader
      loader,
      isBusyTable,

      // Error
      errorContent,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
